/**
 * <gr-fuel-deck-filter>
 * Wires the rarity filter pill row to the deck grid.
 *
 * Expected DOM (within the same component):
 *   .gr-fuel-grid__filter-pill[data-rarity="all|common|uncommon|rare|epic|legendary"]
 *   .gr-fuel-grid[data-active-rarity]            — target grid (sibling, queried by [data-grid])
 *   .gr-fuel-grid__count-visible                 — optional <strong> showing the visible count
 *
 * Click a pill → set [aria-pressed] on it, clear on others, set
 * data-active-rarity on the grid. CSS handles the hide/show. Count is
 * recomputed by counting visible .gr-fuel-grid__item children.
 */

class GrFuelDeckFilter extends HTMLElement {
  #controller = new AbortController();

  /** @type {HTMLElement | null} */
  get #grid() {
    const id = this.dataset.gridId;
    return id ? this.ownerDocument.getElementById(id) : this.querySelector('.gr-fuel-grid');
  }

  /** @type {NodeListOf<HTMLButtonElement>} */
  get #pills() {
    return this.querySelectorAll('.gr-fuel-grid__filter-pill');
  }

  /** @type {HTMLElement | null} */
  get #countEl() {
    return this.querySelector('.gr-fuel-grid__count-visible');
  }

  connectedCallback() {
    const { signal } = this.#controller;
    this.#pills.forEach((pill) => {
      pill.addEventListener('click', this.#handlePillClick, { signal });
    });
    this.#updateCount();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #handlePillClick = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;
    const rarity = target.dataset.rarity || 'all';

    this.#pills.forEach((p) => {
      p.setAttribute('aria-pressed', p === target ? 'true' : 'false');
    });

    const grid = this.#grid;
    if (grid) grid.setAttribute('data-active-rarity', rarity);

    this.#updateCount();
  };

  #updateCount() {
    const countEl = this.#countEl;
    const grid = this.#grid;
    if (!countEl || !grid) return;
    const visible = Array.from(grid.querySelectorAll('.gr-fuel-grid__item')).filter(
      (el) => getComputedStyle(el).display !== 'none'
    ).length;
    countEl.textContent = String(visible);
  }
}

if (!customElements.get('gr-fuel-deck-filter')) {
  customElements.define('gr-fuel-deck-filter', GrFuelDeckFilter);
}
